import { includes } from "lodash";
import { motion, type Variants } from "motion/react";
import { type PropsWithChildren, useEffect, useState } from "react";

import { cn } from "@krea/shared/utils";

import type { IApplyingCompanyExtended } from "@/features/lender-applications/types/applying-company";

const motionItem: Variants = {
  hidden: {
    height: "0px",
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  visible: {
    height: "auto",
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

interface ICompanyStructureItemProps extends PropsWithChildren {
  item: NonNullable<IApplyingCompanyExtended["groupStructure"]>;
  expanded: boolean;
}

const CompanyStructureItem = ({
  item,
  expanded,
  children,
}: ICompanyStructureItemProps) => {
  const [isOpen, setIsOpen] = useState(!!expanded);

  useEffect(() => {
    setIsOpen(expanded);
  }, [expanded]);

  const orgName = item.organisationName;
  const orgNumber = item.organisationNumber;
  const countryCode = item.countryCode;

  const hasChildCompanies = item.children && item.children.length > 0;

  const toggleItem = () => {
    if (hasChildCompanies) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div
      className={cn("mt-4 rounded-sm border px-4 py-4", {
        "border-gray-300  bg-gray-100": item.isCurrentCompany,
        "border-gray-300 bg-white": !item.isCurrentCompany,
      })}
    >
      <button
        type="button"
        className={cn(
          "flex w-full border-0 bg-transparent p-0 text-left",
          hasChildCompanies ? "cursor-pointer" : "cursor-default",
        )}
        onClick={toggleItem}
        disabled={!hasChildCompanies}
      >
        {hasChildCompanies && (
          <div className="mr-3 shrink-0">
            <span
              aria-hidden="true"
              className="mt-1 flex h-6 w-6 items-center justify-center rounded-sm border border-gray-300 bg-white"
            >
              <span className="font-semibold">{isOpen ? "-" : "+"}</span>
            </span>
          </div>
        )}
        <div>
          <div className="text-base-strong mb-1">
            {`${orgName} (${orgNumber}), ${countryCode}`}
          </div>
          <div className="text-base">
            {(() => {
              if (!item.ownedPercentage) {
                return item.isCurrentCompany ? "" : "Parent company";
              }

              return `Ownership: ${item.ownedPercentage}%`;
            })()}
          </div>
        </div>
      </button>

      {hasChildCompanies && (
        <motion.div
          variants={motionItem}
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
          className="overflow-hidden"
        >
          <div className="pl-5">{children}</div>
        </motion.div>
      )}
    </div>
  );
};

interface IProps {
  companyStructure: IApplyingCompanyExtended["groupStructure"];
}

const findPathToCurrentCompany = (
  nodes: NonNullable<IApplyingCompanyExtended["groupStructure"]>[],
  path: string[] = [],
): string[] => {
  for (const node of nodes) {
    const newPath = [...path, node.organisationNumber];

    if (node.isCurrentCompany) {
      return newPath;
    }

    if (node.children && node.children.length > 0) {
      const result = findPathToCurrentCompany(node.children, newPath);

      if (result.length > 0) {
        return result;
      }
    }
  }

  return [];
};

const CompanyStructure = ({ companyStructure }: IProps) => {
  const [isExpanded, setIsExpanded] = useState<string[]>([]);

  useEffect(() => {
    if (companyStructure) {
      const initialGroupStructures = [companyStructure];

      if (companyStructure.children) {
        initialGroupStructures.push(...companyStructure.children);
      }

      setIsExpanded(findPathToCurrentCompany(initialGroupStructures, []));
    }
  }, [companyStructure]);

  const createTree = (
    item: NonNullable<IApplyingCompanyExtended["groupStructure"]>,
    index: number,
  ) => {
    return (
      <CompanyStructureItem
        expanded={includes(isExpanded, item.organisationNumber)}
        key={index}
        item={item}
      >
        {item.children?.map((itm, index) => createTree(itm, index))}
      </CompanyStructureItem>
    );
  };

  if (!companyStructure) {
    return null;
  }

  return (
    <CompanyStructureItem item={companyStructure} expanded>
      {companyStructure.children?.map((item, index) => createTree(item, index))}
    </CompanyStructureItem>
  );
};

export { CompanyStructure };
